import React from 'react';
import { useDrop } from 'react-dnd';
import { PuzzlePiece, ItemTypes } from './PuzzlePiece';
import { GRID_SIZE } from '../../lib/constants';
import { cn } from '../../lib/utils';

export function PiecesTray({ 
  pieces,
  traySlots, 
  pieceSize, 
  isMobile = false,
  isLandscape = false,
  gridSize = GRID_SIZE,
  onReturnToTray,
  useTapMode = false,
  selectedPiece,
  onPieceTap,
  onTrayTap,
  isPieceSelected,
  hintPieceId = null
}) {
  // Calculate dimensions - reduce padding in landscape mode
  const padding = isLandscape ? 12 : 24; // p-1.5 vs p-3
  const boxSize = pieceSize * gridSize + padding + (gridSize - 1) * 2;
  
  // Use traySlots if provided (fixed grid), otherwise fall back to pieces array
  const slots = traySlots || pieces;
  const isFixedGrid = !!traySlots;
  
  // Check if all pieces have been placed
  const allPiecesPlaced = isFixedGrid 
    ? slots.every(s => s === null)
    : pieces.length === 0;
  
  // Check if a board piece is selected (for showing return-to-tray hint)
  const hasBoardPieceSelected = selectedPiece && !selectedPiece.fromTray;
  
  // Drop handler for returning pieces from the board
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.PUZZLE_PIECE,
    drop: (item) => {
      // Only accept pieces that are from the board (not from tray)
      if (!item.fromTray && onReturnToTray) {
        onReturnToTray(item.sourceRow, item.sourceCol);
      }
    },
    canDrop: (item) => {
      // Can only drop pieces that are on the board (not already in tray)
      return !item.fromTray;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  }), [onReturnToTray]);
  
  // Handle tray click in tap mode (for returning pieces from board)
  const handleTrayClick = (e) => {
    if (!useTapMode || !hasBoardPieceSelected) return;
    
    // Don't trigger if clicking on a piece in the tray (that would select it instead)
    if (e.target.closest('.puzzle-piece')) return;
    
    onTrayTap?.();
  };
  
  // Handle empty slot click in tap mode
  const handleSlotClick = (e, slotHasPiece) => {
    if (!useTapMode) return;
    
    // If slot has a piece, let the piece handle the click
    if (slotHasPiece) return;
    
    // If a board piece is selected and this slot is empty, return piece to tray
    if (hasBoardPieceSelected) {
      e.stopPropagation();
      onTrayTap?.();
    }
  };
  
  if (allPiecesPlaced) {
    return (
      <div 
        ref={drop}
        onClick={handleTrayClick}
        className={cn(
          "pieces-tray rounded-xl bg-muted/80 shadow-lg border-2 border-border",
          "backdrop-blur-sm flex items-center justify-center",
          isLandscape ? "p-1.5" : "p-3",
          isMobile && "max-w-[calc(100vw-32px)]",
          isOver && canDrop && "ring-2 ring-primary ring-inset bg-primary/10",
          useTapMode && hasBoardPieceSelected && "ring-2 ring-primary/50 ring-inset cursor-pointer"
        )}
        style={{
          width: boxSize,
          height: boxSize,
          maxWidth: isMobile ? 'calc(100vw - 32px)' : undefined,
        }}
        data-testid="pieces-tray"
      >
        <p className="text-muted-foreground text-sm text-center">
          {useTapMode && hasBoardPieceSelected ? 'Tap here to return piece' : 'All pieces placed!'}
        </p>
      </div>
    );
  }
  
  return (
    <div 
      ref={drop}
      onClick={handleTrayClick}
      className={cn(
        "pieces-tray rounded-xl bg-muted/80 shadow-lg border-2 border-border",
        "backdrop-blur-sm flex items-center justify-center",
        isLandscape ? "p-1.5" : "p-3",
        isMobile && "max-w-[calc(100vw-32px)]",
        isOver && canDrop && "ring-2 ring-primary ring-inset bg-primary/10",
        useTapMode && hasBoardPieceSelected && "ring-2 ring-primary/50 ring-inset"
      )}
      style={{
        width: boxSize,
        height: boxSize,
        maxWidth: isMobile ? 'calc(100vw - 32px)' : undefined,
      }}
      data-testid="pieces-tray"
    >
      <div 
        className="grid gap-0.5"
        style={{
          gridTemplateColumns: `repeat(${gridSize}, ${pieceSize}px)`,
        }}
      >
        {slots.map((slot, index) => (
          <div 
            key={isFixedGrid ? index : (slot?.id ?? index)}
            onClick={(e) => handleSlotClick(e, !!slot)}
            className={cn(
              "relative rounded-sm bg-card/90",
              useTapMode && slot && isPieceSelected?.(slot.id) && "ring-2 ring-primary ring-offset-1",
              slot && hintPieceId === slot.id && "ring-4 ring-yellow-400 ring-offset-2 animate-pulse z-10 shadow-[0_0_20px_rgba(250,204,21,0.7)] scale-105",
              useTapMode && !slot && hasBoardPieceSelected && "cursor-pointer hover:bg-primary/20 border-2 border-dashed border-primary/40"
            )}
            style={{
              width: pieceSize,
              height: pieceSize,
            }}
          >
            {slot && (
              <PuzzlePiece
                piece={slot}
                pieceSize={pieceSize}
                isInTray={true}
                gridSize={gridSize}
                useTapMode={useTapMode}
                isSelected={isPieceSelected?.(slot.id)}
                onTap={() => onPieceTap?.(slot, true)}
                isHinted={hintPieceId === slot.id}
              />
            )}
            {/* Hint overlay for better visibility */}
            {slot && hintPieceId === slot.id && (
              <div className="absolute inset-0 rounded-sm border-4 border-yellow-400 animate-pulse pointer-events-none" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
